<template>
  <div>
    <div class="calculator-container">
      <h1 class="h3">
        Confidence Interval Calculator
      </h1>
      <span class="your-data">
        Your Data
      </span>
      <Dropdown
        class="calculation-method"
        calculator
        :selected-title="firstMethodName"
        :selected="selectedCalculationMethod"
        :radio-buttons="{ readyData: firstMethodName, rawData: 'Raw Data' }"
        @change="currentSelectedCalculationMethod"
      />
      <form @submit.prevent="calculations">
        <div v-if="selectedCalculationMethod === 'readyData'" class="sample-inputs">
          <div>
            <div class="label-tooltip">
              <label class="sample-mean-label">
                Sample Mean <span>(x̄)</span>
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip>
                  <span>Provide a positive numerical value.</span>
                </Tooltip>
              </div>
            </div>
            <input
              v-model="mean" type="text"
              :class="{ 'invalid-value': meanAlert }"
            >
            <div v-if="meanAlert" class="error">
              {{ meanAlert }}
            </div>
          </div>
          <div class="sample-size">
            <div class="label-tooltip">
              <label>
                Sample Size (n)
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip>
                  <span>Provide a positive integer as sample size value.</span>
                </Tooltip>
              </div>
            </div>
            <input
              v-model="size" type="text"
              :class="{ 'invalid-value': sizeAlert }"
            >
            <div v-if="sizeAlert" class="error">
              {{ sizeAlert }}
            </div>
          </div>
        </div>
        <div v-else-if="selectedCalculationMethod === 'rawData'" class="raw-data">
          <div class="label-tooltip">
            <label class="type-data">
              Type Your Data
            </label>
            <div class="icon-info-tooltip">
              <IconInfo />
              <Tooltip>
                <span>Input the data separated by comma, space or enter.</span>
              </Tooltip>
            </div>
          </div>
          <div class="textarea-border" :class="{ 'invalid-value': textareaAlert }">
            <textarea
              v-model="rawDataMean"
              cols="30" rows="10"
              placeholder="7,1,6,2,11

7 1 6 2 11

7
1
6"
            />
          </div>
          <div v-if="textareaAlert" class="error">
            {{ textareaAlert }}
          </div>
        </div>
        <div class="standard-deviation">
          <div class="question-dropdown" :class="{ 'raw-data-version': hidePopulationSampleInput }">
            <label>
              Do you know the population standard deviation (σ)?
            </label>
            <Dropdown
              calculator
              class="small-dropdown"
              selected-title="Yes"
              :selected="selectedAnswer"
              :radio-buttons="{ Yes: 'Yes', No: 'No' }"
              @change="currentSelectedAnswer"
            />
          </div>
          <div v-if="!hidePopulationSampleInput" class="population-sample">
            <div class="label-tooltip">
              <label>
                {{ populationOrSample[0] }}
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip :class="{ 'confidence-population-tooltip': selectedCalculationMethod === 'readyData' }">
                  <span v-if="selectedCalculationMethod === 'readyData'">Provide a numerical and positive standard deviation value.</span>
                  <span v-else>Provide a numerical positive value.</span>
                </Tooltip>
              </div>
            </div>
            <input
              v-model="populationSampleData[populationOrSample[1]]" type="text"
              :class="{ 'invalid-value': populationSampleData[populationOrSample[2]] }"
            >
            <div
              v-if="populationSampleData[populationOrSample[2]] && !hidePopulationSampleInput"
              class="error"
            >
              {{ populationSampleData[populationOrSample[2]] }}
            </div>
          </div>
        </div>
        <div
          class="confidence-level-decimal-places"
        >
          <div>
            <div class="label-tooltip">
              <label>
                Confidence Level
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip class="confidence-population-tooltip">
                  <span>Provide a numerical confidence level value between 0 and 100%.</span>
                </Tooltip>
              </div>
            </div>
            <div class="input-dropdown">
              <input
                v-model="selectedConfidenceLevel" type="text"
                :class="{ 'invalid-value': confidenceLevelAlert, 'dropdown-opened': dropdownOpened }"
                @blur="percentageSymbolCheck"
              >
              <Dropdown
                calculator
                calculator-input-dropdown-combination
                class="small-dropdown"
                :class="{ 'add-background': arrowHover, 'invalid-value': confidenceLevelAlert }"
                :selected="selectedConfidenceLevel"
                :radio-buttons="{ '90%': '90%', '95%': '95%', '99%': '99%' }"
                @change="currentSelectedConfidenceLevel"
                @arrowHover="arrowHover = true"
                @arrowLeave="arrowHover = false"
                @arrowClicked="dropdownOpened = !dropdownOpened"
                @dropdownClosed="dropdownOpened = false"
              />
            </div>
            <div v-if="confidenceLevelAlert" class="error">
              {{ confidenceLevelAlert }}
            </div>
          </div>
          <div class="decimal-places">
            <div class="label-tooltip">
              <label>
                Decimal Places
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip>
                  <span>Choose the number of decimal places.</span>
                </Tooltip>
              </div>
            </div>
            <Dropdown
              calculator
              calculator-decimal-places
              class="small-dropdown"
              :selected="selectedDecimalPlaces"
              :radio-buttons="{ 1: '1', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9' }"
              @change="currentSelectedDecimalPlace"
            />
          </div>
        </div>
        <div class="buttons">
          <button class="btn-primary calculate">
            Calculate
          </button><button
            class="btn-primary-light reset"
            @click="reset"
          >
            Reset
          </button>
          <button class="load-example btn-link-accent" @click="loadExample">
            Load Example
          </button>
        </div>
      </form>
    </div>
    <div v-if="!confidenceInterval" class="instructions-container">
      <h3 class="h4">
        How to Use the Confidence Interval Calculator
      </h3>
      <h4 class="h6">
        Your Data:
      </h4>
      <ol>
        <li class="bottom-margins">
          <span class="colored-texts"><nuxt-link to="/tutorials/statistics-tutorials/mean-median-and-mode/" target="_blank">
            Mean
          </nuxt-link>, <nuxt-link to="/tutorials/statistics-tutorials/central-limit-theorem/" target="_blank">
            Variance
          </nuxt-link></span>, <span class="bolded-texts">and Sample Size:</span> Input the arithmetic mean, variance, and sample size of your data.
        </li>
        <li class="bottom-margins">
          <span class="bolded-texts">Raw Data:</span> If you opt for raw data, the calculator will find the arithmetic mean, standard deviation, and sample size for you. Please note that each data point should be separated by a comma.
        </li>
        <li> <span class="bolded-texts">Decimal Places:</span> Numbers that are greater than 0 are rounded to a specific number of decimal places. If the number of digits is negative, the number is rounded to the left of the decimal point. For example, when 3 decimal places are chosen, 94.1234 will be rounded to 94.123 and 0.004321 will be rounded to 0.004.</li>
      </ol>
      <h4 class="psd-question h6">
        Do you know the population standard deviation (𝝈)?
      </h4>
      <p class="bottom-margins">
        Choose <span class="bolded-texts">Yes</span>, if you know the population standard deviation. Then input it in the space on the right. The calculator will use the standard normal distribution, z-score, and population standard deviation (σ).
      </p>
      <p>Choose <span class="bolded-texts">No</span>, if you don’t know the population standard deviation. Leave the space on the right blank. The calculator will use the t-distribution, t-statistic, and sample standard deviation (s).</p>
    </div>
    <div
      v-show="confidenceInterval" id="summary" class="summary-container"
    >
      <h4>Summary:</h4>
      <div class="results">
        <div><span class="result-name col-8 sm-8">Confidence Interval:</span> <span class="result-value">{{ confidenceInterval }}</span></div>
        <div class="middle">
          <span class="result-name col-8 sm-8">Margin of error (MOE):</span> <span class="result-value">{{ marginOfError }}</span>
        </div>
        <div><span class="result-name col-8 sm-8">Standard Error (S.E):</span> <span class="result-value">{{ standardError }}</span></div>
      </div>
    </div>
    <div v-if="confidenceInterval" class="interpretation-container">
      <h4>Interpretation:</h4>
      <p v-if="knownOrUnknownPsd === 'Yes'">
        Since the population standard deviation is <span class="bolded-texts">known</span>, the Confidence Interval Calculator uses the <span class="bolded-texts">standard normal distribution</span>, z-score, and population standard deviation (σ).
      </p>
      <p v-else>
        Since the population standard deviation is <span class="bolded-texts">unknown</span>, the Confidence Interval Calculator uses the Student’s <span class="bolded-texts">t-distribution</span> with n-1 degrees of freedom, the t-statistic, and sample standard deviation (s).
      </p>
      <p>
        A confidence interval indicates where the population parameter is likely to be. You can be {{ usedConfidenceSignificanceLevel[0] }}% confident that the true population mean (µ) falls between {{ lowerBound }} and {{ upperBound }}.  And there is {{ usedConfidenceSignificanceLevel[1] }}%  (α) chance that the confidence interval does not contain the true population mean (µ).
      </p>
    </div>
  </div>
</template>

<script>
import Dropdown from '@itbpbg/lms-components/src/components/Dropdown.vue';
import Tooltip from '@itbpbg/lms-components/src/components/Tooltip.vue';
import IconInfo from '@itbpbg/lms-components/src/assets/svg/icon-info.svg?vue-component';
import jStat from 'jstat';

export default {
  components: {
    Dropdown,
    Tooltip,
    IconInfo,
  },
  mixin: [global],
  data() {
    return {
      selected: '',
      selectedCalculationMethod: 'readyData',
      selectedConfidenceLevel: '95%',
      selectedDecimalPlaces: '4',
      confidenceLevelInteger: '',
      selectedAnswer: 'No',
      mean: '',
      rawDataMeanArray: [],
      rawDataMean: '',
      size: '',
      populationSampleData: {
        populationS: '',
        sampleS: '',
        populationAlert: false,
        sampleAlert: false,
      },
      meanAlert: false,
      sizeAlert: false,
      confidenceLevelAlert: false,
      textareaAlert: false,
      arrowHover: false,
      confidenceInterval: '',
      marginOfError: '',
      standardError: '',
      knownOrUnknownPsd: '',
      lowerBound: '',
      upperBound: '',
      usedConfidenceSignificanceLevel: [],
      resultNumber: 0,
      dropdownOpened: false,
      zScoreTable: {
        99: 2.575829,
        98: 2.326348,
        97: 2.17009,
        96: 2.053749,
        95: 1.959964,
        94: 1.880794,
        93: 1.811911,
        92: 1.750686,
        91: 1.695398,
        90: 1.644854,
        89: 1.598193,
        88: 1.554774,
        87: 1.514102,
        86: 1.475791,
        85: 1.439531,
        84: 1.405072,
        83: 1.372204,
        82: 1.340755,
        81: 1.310579,
        80: 1.281552,
        79: 1.253565,
        78: 1.226528,
        77: 1.200359,
        76: 1.174987,
        75: 1.150349,
        74: 1.126391,
        73: 1.103063,
        72: 1.080319,
        71: 1.058122,
        70: 1.036433,
        69: 1.015222,
        68: 0.994458,
        67: 0.974114,
        66: 0.954165,
        65: 0.934589,
        64: 0.915365,
        63: 0.896473,
        62: 0.877896,
        61: 0.859617,
        60: 0.841621,
        59: 0.823894,
        58: 0.806421,
        57: 0.789192,
        56: 0.772193,
        55: 0.755415,
        54: 0.738847,
        53: 0.722479,
        52: 0.706303,
        51: 0.690309,
        50: 0.67449,
        49: 0.658838,
        48: 0.643345,
        47: 0.628006,
        46: 0.612813,
        45: 0.59776,
        44: 0.582842,
        43: 0.568051,
        42: 0.553385,
        41: 0.538836,
        40: 0.524401,
        39: 0.510073,
        38: 0.49585,
        37: 0.481727,
        36: 0.467699,
        35: 0.453762,
        34: 0.439913,
        33: 0.426148,
        32: 0.412463,
        31: 0.398855,
        30: 0.38532,
        29: 0.371856,
        28: 0.358459,
        27: 0.345126,
        26: 0.331853,
        25: 0.318639,
        24: 0.305481,
        23: 0.292375,
        22: 0.279319,
        21: 0.266311,
        20: 0.253347,
        19: 0.240426,
        18: 0.227545,
        17: 0.214702,
        16: 0.201893,
        15: 0.189118,
        14: 0.176374,
        13: 0.163658,
        12: 0.150969,
        11: 0.138304,
        10: 0.125661,
        9: 0.113039,
        8: 0.100434,
        7: 0.0878448,
        6: 0.0752699,
        5: 0.0627068,
        4: 0.0501536,
        3: 0.0376083,
        2: 0.0250689,
        1: 0.0125335,
      },
    };
  },
  computed: {
    populationOrSample() {
      return this.selectedAnswer === 'Yes' || this.selectedAnswer === ''
        ? ['Population Standard Deviation (σ)', 'populationS', 'populationAlert']
        : ['Sample Standard Deviation (s)', 'sampleS', 'sampleAlert'];
    },
    hidePopulationSampleInput() {
      return this.selectedCalculationMethod === 'rawData' && this.selectedAnswer === 'No';
    },
    firstMethodName() {
      return this.$store.state.layout !== 'desktop' ? 'Mean, SD and n' : 'Mean, Standard Deviation and Sample Size';
    },
    hideResultVariablesArray() {
      return [
        this.mean,
        this.size,
        this.populationSampleData.sampleS,
        this.populationSampleData.populationS,
        this.selectedConfidenceLevel,
        this.selectedDecimalPlaces,
        this.rawDataMean,
        this.selectedAnswer,
      ];
    },
  },
  watch: {
    selectedCalculationMethod() {
      this.defaultingInput();
    },
    hideResultVariablesArray() {
      this.hideResults();
    },
  },
  mounted() {
    this.$store.commit('calculators/result', false);
  },
  methods: {
    async scrollToSection() {
      this.$nextTick(() => {
        let yOffset;
        if (this.$store.state.layout === 'desktop') {
          yOffset = -90;
        } else {
          yOffset = this.$config.project === 'lms' ? -64 : -68;
        }

        const section = 'summary';
        const element = document.querySelector(`#${section}`);
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        setTimeout(() => {
          this.clickedSection = section;
          window.scrollTo({ top: y, behavior: 'smooth' });

          setTimeout(() => {
            this.clickedSection = null;
          }, 500);
        }, 200);
      });
    },
    percentageSymbolCheck() {
      const validPercentageTest = /^[1-9][0-9]{0,1}$/;
      if (validPercentageTest.test(this.selectedConfidenceLevel)) {
        this.selectedConfidenceLevel += '%';
      }
    },
    reset(e) {
      this.selectedCalculationMethod = 'readyData';
      this.defaultingInput();
      e.preventDefault();
    },
    defaultingInput() {
      this.mean = '';
      this.rawDataMean = '';
      this.size = '';
      this.populationSampleData[this.populationOrSample[1]] = '';
      this.removeAlerts();
      this.confidenceInterval = '';
      this.selectedConfidenceLevel = '95%';
      this.selectedDecimalPlaces = '4';
      this.selectedAnswer = 'No';
      this.$store.commit('calculators/result', false);
    },
    hideResults() {
      this.$store.commit('calculators/result', false);
      this.confidenceInterval = '';
      this.removeAlerts();
    },
    loadExample(e) {
      this.hideResults();
      this.removeAlerts();
      this.selectedConfidenceLevel = '95%';
      this.selectedDecimalPlaces = '4';

      if (this.selectedCalculationMethod === 'readyData') {
        this.mean = '92.53';
        this.size = '9';
        if (this.selectedAnswer === 'No') {
          this.populationSampleData[this.populationOrSample[1]] = '13.93';
        } else {
          this.populationSampleData[this.populationOrSample[1]] = '15';
        }
      } else {
        this.rawDataMean = '78.00\n90.00\n75.00\n117.00\n105.00\n96.00\n89.50\n102.30\n80.00';
        if (this.selectedAnswer === 'Yes') {
          this.populationSampleData[this.populationOrSample[1]] = '15';
        }
      }

      e.preventDefault();
    },
    removeAlerts() {
      const alertsArray = ['meanAlert', 'sizeAlert', this.populationOrSample[2], 'confidenceLevelAlert', 'textareaAlert'];

      for (let i = 0; i < alertsArray.length; i += 1) {
        if (alertsArray[i] === 'populationAlert' || alertsArray[i] === 'sampleAlert') {
          this.populationSampleData[alertsArray[i]] = false;
        } else {
          this[alertsArray[i]] = false;
        }
      }
    },
    currentSelectedCalculationMethod(currentMethod) {
      this.selectedCalculationMethod = currentMethod;
    },
    currentSelectedConfidenceLevel(selectedConfidenceLevel) {
      this.selectedConfidenceLevel = selectedConfidenceLevel;
    },
    currentSelectedDecimalPlace(selectedDecimalPlaces) {
      this.selectedDecimalPlaces = selectedDecimalPlaces;
    },
    currentSelectedAnswer(selectedAnswer) {
      this.selectedAnswer = selectedAnswer;
      this.populationSampleData[this.populationOrSample[1]] = '';
      this.populationSampleData[this.populationOrSample[2]] = false;
    },
    inputValidation() {
      this.removeAlerts();

      let validationResult = true;
      this.selectedConfidenceLevel = this.selectedConfidenceLevel.trim();
      this.populationSampleData[this.populationOrSample[1]] = this.populationSampleData[this.populationOrSample[1]].trim();
      const confidenceLevelRegEx = /^[1-9][0-9]{0,1}%$/;
      const confidenceLevelRegExTest = confidenceLevelRegEx.test(this.selectedConfidenceLevel);

      if (confidenceLevelRegExTest) {
        this.confidenceLevelInteger = parseFloat(this.selectedConfidenceLevel);
      } else {
        if (this.selectedConfidenceLevel === '') {
          this.confidenceLevelInteger = '';
        } else {
          this.confidenceLevelInteger = -1;
        }
        validationResult = false;
      }

      if (this.selectedCalculationMethod === 'readyData') {
        this.mean = this.mean.trim();
        this.size = this.size.trim();

        const alertsArray = ['meanAlert', 'sizeAlert', this.populationOrSample[2], 'confidenceLevelAlert'];
        const inputValuesArray = [this.mean, this.size, this.populationSampleData[this.populationOrSample[1]], this.confidenceLevelInteger];
        const invalidValuesArray = inputValuesArray.map((value) => ((value === '' || (Number.isNaN(Number(value)) || value < 0)) ? value : 'valid'));

        for (let i = 0; i < invalidValuesArray.length; i += 1) {
          if (invalidValuesArray[i] !== 'valid') {
            if (invalidValuesArray[i] === '') {
              if (alertsArray[i] === 'populationAlert' || alertsArray[i] === 'sampleAlert') {
                this.populationSampleData[alertsArray[i]] = 'Field required';
              } else {
                this[alertsArray[i]] = 'Field required';
              }
            } else if (alertsArray[i] === 'populationAlert' || alertsArray[i] === 'sampleAlert') {
              this.populationSampleData[alertsArray[i]] = 'Invalid value';
            } else {
              this[alertsArray[i]] = 'Invalid value';
            }

            validationResult = false;
          }
        }

        return validationResult;
      }

      this.rawDataMeanArray = this.rawDataMean.split(/,|\s+|\n+/).filter((value) => value !== '');

      if (this.rawDataMeanArray.length === 0) {
        this.textareaAlert = 'Field required';
        this.rawDataMean = '';
        validationResult = false;
      } else {
        for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
          if (this.rawDataMeanArray[i] < 0 || Number.isNaN(Number(this.rawDataMeanArray[i]))) {
            this.textareaAlert = 'Invalid value';
            validationResult = false;
          }
        }
      }

      if (this.populationSampleData[this.populationOrSample[1]] === '') {
        this.populationSampleData[this.populationOrSample[2]] = 'Field required';
      } else if (this.populationSampleData[this.populationOrSample[1]] < 0
       || Number.isNaN(Number(this.populationSampleData[this.populationOrSample[1]]))) {
        this.populationSampleData[this.populationOrSample[2]] = 'Invalid Value';
      }

      if (this.confidenceLevelInteger === '') {
        this.confidenceLevelAlert = 'Field required';
      } else if (this.confidenceLevelInteger < 0
       || Number.isNaN(Number(this.confidenceLevelInteger))) {
        this.confidenceLevelAlert = 'Invalid Value';
      }

      if (this.populationSampleData.populationAlert !== false) {
        validationResult = false;
      }

      return validationResult;
    },
    calculations() {
      if (!this.inputValidation()) {
        return;
      }

      let mean = 0;
      let size = 0;
      let levelOfSignificance = 0;
      let standardDeviation = this.populationSampleData[this.populationOrSample[1]];
      let currentScore = 0;

      if (this.selectedCalculationMethod === 'readyData') {
        mean = Number(this.mean);
        size = Number(this.size);
      } else {
        for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
          mean += Number(this.rawDataMeanArray[i]);
        }

        mean /= this.rawDataMeanArray.length;
        size = this.rawDataMeanArray.length;
      }

      if (this.selectedAnswer === 'Yes') {
        levelOfSignificance = 1 - this.confidenceLevelInteger / 100;
        currentScore = this.zScoreTable[this.confidenceLevelInteger];
      } else {
        levelOfSignificance = 1 - this.confidenceLevelInteger / 100;
        const probability = levelOfSignificance / 2;
        currentScore = Math.abs(jStat.studentt.inv(probability, size - 1));

        if (this.selectedCalculationMethod === 'rawData') {
          standardDeviation = 0;

          for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
            standardDeviation += (Number(this.rawDataMeanArray[i]) - mean) ** 2;
          }

          standardDeviation = Math.sqrt(standardDeviation / (size - 1));
        }
      }

      this.standardError = standardDeviation / Math.sqrt(size);
      this.marginOfError = currentScore * this.standardError;
      this.confidenceInterval = `[${(mean - this.marginOfError).toFixed(this.selectedDecimalPlaces)}, ${(mean + this.marginOfError).toFixed(this.selectedDecimalPlaces)}]`;
      this.lowerBound = (mean - this.marginOfError).toFixed(this.selectedDecimalPlaces);
      this.upperBound = (mean + this.marginOfError).toFixed(this.selectedDecimalPlaces);
      this.standardError = this.standardError.toFixed(this.selectedDecimalPlaces);
      this.marginOfError = this.marginOfError.toFixed(this.selectedDecimalPlaces);
      this.knownOrUnknownPsd = this.selectedAnswer;
      this.usedConfidenceSignificanceLevel = [this.confidenceLevelInteger, 100 - this.confidenceLevelInteger];
      this.$store.commit('calculators/result', this.resultNumber += 1);
      this.calculationData(mean, size, standardDeviation, levelOfSignificance, currentScore);
      this.scrollToSection();
    },
    calculationData(mean, size, standardDeviation, levelOfSignificance, currentScore) {
      const onlyZerosTest = /^\d+.[0]+$/;
      const decimalTest = /^\d+\.{1}?\d+$/;
      let rawDataMeanArray = this.rawDataMeanArray.map((num) => (onlyZerosTest.test(num) ? Math.trunc(num) : num));
      rawDataMeanArray = this.rawDataMeanArray.map((num) => (decimalTest.test(num) ? Number(num).toFixed(2) : num));

      this.$store.commit('calculators/calculationData',
        {
          method: this.selectedCalculationMethod,
          answer: this.selectedAnswer,
          confidenceLevel: this.confidenceLevelInteger / 100,
          mean: mean.toFixed(2),
          size,
          standardDeviation: Number(standardDeviation).toFixed(2),
          rawDataMeanArray,
          rawDataMeanArrayActualInput: this.rawDataMeanArray.map(Number),
          marginOfError: Number(this.marginOfError).toFixed(2),
          standardError: Number(this.standardError).toFixed(2),
          levelOfSignificance: levelOfSignificance.toFixed(2),
          confidenceInterval: this.confidenceInterval,
          currentScore: currentScore.toFixed(3),
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.calculator-container {
  .standard-deviation {
    min-height: 90px;
    display: flex;
    padding: 0;
    gap: 30px;
    margin-top: var(--spacing-30);

    .question-dropdown {
      .dropdown::v-deep.open .list {
        top: 102.9%;
      }

      label {
        display: inline-block;
        margin-bottom: var(--spacing-6);
        line-height: 120%;
      }

      &.raw-data-version {
        width: calc(50% - 15px);
      }
    }

    .population-sample {
      .label-tooltip {
        display: flex;
        height: 33.59px;
        align-items: flex-end;

        .icon-info-tooltip {
          height: 17px;
        }
      }

      input {
        height: 52px;
        border: 1px solid rgba(0, 12, 31, 0.18);
        border-radius: 5px;
      }

      input:focus {
        border-color: var(--main-dark);
      }

      input.invalid-value {
        border-color: #E04A4A;
      }
    }
  }

  .confidence-level-decimal-places {
    display: flex;
    gap: 30px;
    margin: var(--spacing-36) 0;

    .decimal-places {
      width: 100%;
      margin: 0;

      .dropdown::v-deep.open .list {
        top: 101.9%;
      }
    }

    .input-dropdown {
      display: flex;
      position: relative;

      .dropdown.small-dropdown {
        width: 32px;
        border-left: unset;
        position: unset;
        border-radius: 0 5px 5px 0;

        &.add-background {
          background: var(--main-light-shade-2);
          border: 1px solid rgba(0, 12, 31, 0.18);
        }

        &.open {
          border: 1px solid var(--main-dark);
          border-bottom: 0;
          background: var(--main-light-shade-2);
          border-radius: 0 5px 0 0;
        }

        &.invalid-value {
          border-color: #E04A4A;
        }
      }

      .dropdown::v-deep {
        height: 52px;
        border-radius: 0 5px 5px 0;
        border: 1px solid rgba(0, 12, 31, 0.18);

        label.active span {
          font-weight: 600;
        }

        .list {
          left: unset;
        }

        .list-head {
          width: 32px;
          height: 100%;
          padding: 0;
          justify-content: center;
          max-width: unset;

          svg {
            position: unset;
          }

          span {
            display: none;
          }
        }
      }

      input {
        border-radius: 5px 0 0 5px;
        border: 1px solid rgba(0, 12, 31, 0.18);
        border-right: unset;

        &.dropdown-opened {
          border-radius: 5px 0 0 0;
          border-bottom: 0;
        }
      }

      input:focus {
        border: 1px solid var(--main-dark);
      }

      input.invalid-value {
        border-color: #E04A4A;
      }
    }
  }
}

.instructions-container {
  padding: var(--spacing-40) var(--spacing-30);
  margin: var(--spacing-48) 0 var(--spacing-30);
  background: var(--main-light);
  border: 0.83px solid rgba(0, 12, 31, 0.18);
  border-radius: 10px;

  p {
    margin: 0;
  }

  .bolded-texts {
    font-weight: 600;
  }

  .bottom-margins {
    margin-bottom: var(--spacing-20);
  }

  .h4 {
    margin-bottom: var(--spacing-40);
  }

  .psd-question {
    margin-top: var(--spacing-40);
  }

  a {
    font-weight: 600;
    text-decoration: underline;
    display: inline-block;
  }

  .colored-texts {
    font-weight: 600;
    color: var(--accent-shade-3);
  }
}

@include bp($bp-mobile) {
  .calculator-container {
    .population-sample {
      .label-tooltip .icon-info-tooltip {
        &:hover {
          .tooltip {
            left: -388%;

            &:after {
              left: 70%;
            }
          }
        }
      }
    }
  }

  .instructions-container {
    background: unset;
    border: unset;
    padding: 0 var(--spacing-24) 0;
    margin: var(--spacing-60) 0 0;
  }
}

@import '../../../../assets/sass/ds/calculators/calculators.scss';
</style>
